import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, ChevronRight, ChevronLeft, Check, Upload, Sparkles } from 'lucide-react';
import {
  fetchAdAccounts, fetchPages, createCampaign, createAdSet, uploadCreativeImage, uploadCreativeVideo, createAd,
  searchInterests, searchLocations, searchLanguages, fetchCustomAudiences, fetchReachEstimate, generateAdCopy,
} from '../../redux/metaAdsSlice';
import { getCurrencySymbol } from '../../utils/currency';
import api from '../../services/api';

const INPUT = 'w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] text-[#2E2E2E] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]';
const LABEL = 'block text-[11px] font-semibold text-[#706E6B] mb-1';
const STEPS = ['Campaign', 'Audience', 'Creative'];
// Meta's own ad_format enum for generatepreviews/previews — a small,
// representative subset covering the placements most SMB campaigns
// actually use, not Meta's full list of ~30 formats.
const PREVIEW_FORMATS = [
  { value: 'MOBILE_FEED_STANDARD', label: 'Facebook Feed (Mobile)' },
  { value: 'DESKTOP_FEED_STANDARD', label: 'Facebook Feed (Desktop)' },
  { value: 'INSTAGRAM_STANDARD', label: 'Instagram Feed' },
  { value: 'INSTAGRAM_STORY', label: 'Instagram Story' },
  { value: 'FACEBOOK_STORY_MOBILE', label: 'Facebook Story' },
  { value: 'MARKETPLACE_MOBILE', label: 'Facebook Marketplace' },
];

const PLACEMENTS = [
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'messenger', label: 'Messenger' },
  { id: 'audience_network', label: 'Audience Network' },
];

// Meta requires one of these three declared whenever an ad could relate to
// credit, jobs, housing, or social/political issues — an undeclared ad in
// these categories gets rejected (or the whole ad account restricted).
// "none" is the common case and is what most SMB ads actually are.
const SPECIAL_AD_CATEGORIES = [
  { id: 'NONE', label: 'None of these' },
  { id: 'CREDIT', label: 'Credit (loans, credit cards, financial products)' },
  { id: 'EMPLOYMENT', label: 'Employment (job ads, recruiting)' },
  { id: 'HOUSING', label: 'Housing (real estate, rentals, mortgages)' },
  { id: 'ISSUES_ELECTIONS_POLITICS', label: 'Social issues, elections or politics' },
];

// A lead ad's CTA has to make sense for "submit a form," not "click through
// to a link" — Meta doesn't reject a mismatched CTA outright, but it reads
// wrong to the person seeing the ad and confuses what happens on tap. Kept
// as two short, distinct lists rather than one list with a note, so a
// mismatched pairing can't be picked in the first place.
const CTA_OPTIONS_LEAD = [
  { value: 'INTERESTED', label: "I'm interested" },
  { value: 'APPLY_NOW', label: 'Apply Now' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'GET_OFFER', label: 'Get offer' },
  { value: 'GET_QUOTE', label: 'Get quote' },
  { value: 'SEE_DETAILS', label: 'See details' },
  { value: 'LEARN_MORE', label: 'Learn more' },
  { value: 'SUBSCRIBE', label: 'Subscribe' },
  { value: 'BOOK_NOW', label: 'Book now' },
  { value: 'SIGN_UP', label: 'Sign Up' },
];
// Click-to-WhatsApp ads have a single button that opens a chat with your number.
const CTA_OPTIONS_WHATSAPP = [{ value: 'WHATSAPP_MESSAGE', label: 'Send WhatsApp message' }];
const CTA_OPTIONS_LINK = [
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'SHOP_NOW', label: 'Shop Now' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'CONTACT_US', label: 'Contact Us' },
  { value: 'BOOK_NOW', label: 'Book Now' },
  { value: 'ORDER_NOW', label: 'Order Now' },
  { value: 'GET_QUOTE', label: 'Get Quote' },
  { value: 'APPLY_NOW', label: 'Apply Now' },
  { value: 'SUBSCRIBE', label: 'Subscribe' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'GET_OFFER', label: 'Get offer' },
  { value: 'SEE_DETAILS', label: 'See details' },
  { value: 'INTERESTED', label: "I'm interested" },
  { value: 'CALL_NOW', label: 'Call Now (dials a phone number)' },
];

// Digits with an optional leading +, 7-15 digits — same rule as the server.
const isValidPhone = (value) => /^\+?\d{7,15}$/.test(String(value || '').replace(/[\s().-]/g, ''));

// A destination link that isn't a real URL (most commonly a phone number
// typed into this field, confirmed happening in production) gets silently
// rejected by Meta at publish time with no clear error — validating here,
// before the ad is even created, catches it immediately instead.
// Meta requires a lead ad to point at the advertiser's own website — not a Facebook or
// Instagram page. Same rule as the server (constants/cta.js isExternalHttpUrl).
const META_OWNED_HOSTS = /(^|\.)(facebook\.com|fb\.com|fb\.me|instagram\.com|messenger\.com|whatsapp\.com|wa\.me)$/i;
function isExternalHttpUrl(value) {
  try {
    const u = new URL(value);
    return (u.protocol === 'http:' || u.protocol === 'https:') && !META_OWNED_HOSTS.test(u.hostname);
  } catch {
    return false;
  }
}

function isValidHttpUrl(value) {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

// Meta's real per-account/currency minimum isn't exposed by a simple read
// endpoint, so this is a conservative, currency-aware sanity floor to catch
// the "₹100/day accepted with zero warning" case — not Meta's own minimum,
// which is enforced (and will still error clearly) at creation time
// regardless.
const LOW_BUDGET_FLOOR = { INR: 300, USD: 5, EUR: 5, GBP: 4 };

// Meta's own "standard" Instant Form field types — prefilled from the
// person's Facebook profile where possible, so they're one tap instead of
// typing. FULL_NAME/PHONE/EMAIL stay the default (matches what the form
// already asked for before this), the rest are opt-in additions.
const STANDARD_FIELD_OPTIONS = [
  { type: 'FULL_NAME', label: 'Full Name' },
  { type: 'PHONE', label: 'Phone Number' },
  { type: 'EMAIL', label: 'Email' },
  { type: 'CITY', label: 'City' },
  { type: 'COMPANY_NAME', label: 'Company Name' },
  { type: 'JOB_TITLE', label: 'Job Title' },
  { type: 'WORK_EMAIL', label: 'Work Email' },
];

// Meta rejects an ad set whose optimization_goal doesn't match its
// campaign's objective (confirmed live — a TRAFFIC-objective campaign
// rejects a LEAD_GENERATION ad set outright). Every objective this wizard
// offers needs its own valid default rather than always hardcoding
// LEAD_GENERATION, which silently broke every objective but Lead Generation.
const OPTIMIZATION_GOAL_BY_OBJECTIVE = {
  OUTCOME_LEADS: 'LEAD_GENERATION',
  OUTCOME_TRAFFIC: 'LINK_CLICKS',
  OUTCOME_ENGAGEMENT: 'POST_ENGAGEMENT',
  OUTCOME_AWARENESS: 'REACH',
  OUTCOME_SALES: 'OFFSITE_CONVERSIONS',
  OUTCOME_APP_PROMOTION: 'APP_INSTALLS',
};

const CONVERSION_EVENT_OPTIONS = [
  ['PURCHASE', 'Purchase'], ['LEAD', 'Lead'], ['COMPLETE_REGISTRATION', 'Registration'], ['ADD_TO_CART', 'Add to cart'],
  ['INITIATE_CHECKOUT', 'Start checkout'], ['CONTACT', 'Contact'], ['SUBMIT_APPLICATION', 'Application'],
];

// What Meta accepts for a picture, checked before upload so a bad file fails here with a plain reason
// instead of after a round trip (or, worse, at delivery). Ratios outside 4:5 to 1.91:1 get cropped or rejected.
const MAX_IMAGE_BYTES = 30 * 1024 * 1024;
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
function validateImageFile(file) {
  return new Promise((resolve) => {
    if (!/^image\/(jpeg|png)$/.test(file.type)) return resolve({ ok: false, error: 'Use a JPG or PNG picture.' });
    if (file.size > MAX_IMAGE_BYTES) return resolve({ ok: false, error: 'That picture is over 30 MB — use a smaller one.' });
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { naturalWidth: width, naturalHeight: height } = img;
      const ratio = width / height;
      if (Math.min(width, height) < 600) return resolve({ ok: false, error: `That picture is only ${width}×${height}px — Meta needs at least 600px on the short side.` });
      if (ratio < 0.8 || ratio > 1.91) return resolve({ ok: false, error: `That picture's shape (${ratio.toFixed(2)}:1) will be cropped badly. Use between 4:5 (tall) and 1.91:1 (wide); square (1:1) is safest.` });
      resolve({ ok: true, width, height });
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve({ ok: false, error: 'That file could not be read as a picture.' }); };
    img.src = url;
  });
}

export default function MetaAdsCampaignWizard({ onClose, onDone }) {
  const dispatch = useDispatch();
  const { adAccounts, pages } = useSelector((s) => s.metaAds);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [campaign, setCampaign] = useState({
    adAccountId: '', name: '', objective: 'OUTCOME_LEADS', specialAdCategories: [], specialAdCategoryCountry: 'IN',
    // Campaign Budget Optimization — off by default, matching this app's
    // long-standing ad-set-level-budget behavior. When on, the fields below
    // replace the ad set's own budget/bid-strategy fields in the next step.
    cbo: false, dailyBudget: '', lifetimeBudget: '', bidStrategy: 'LOWEST_COST_WITHOUT_CAP', bidAmount: '', endTime: '',
  });
  const [cboBudgetType, setCboBudgetType] = useState('daily'); // 'daily' | 'lifetime'
  const [createdCampaignId, setCreatedCampaignId] = useState(null);

  const [adSet, setAdSet] = useState({
    name: '', dailyBudget: 10, lifetimeBudget: '', ageMin: 18, ageMax: 65, genders: [], startTime: '', endTime: '',
    bidStrategy: 'LOWEST_COST_WITHOUT_CAP', bidAmount: '', optimizationGoal: 'LEAD_GENERATION',
  });
  // Empty = Meta automatic placements (recommended); otherwise the user's picks.
  const [placements, setPlacements] = useState([]);
  const [interestQuery, setInterestQuery] = useState('');
  const [interestOptions, setInterestOptions] = useState([]);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [locationQuery, setLocationQuery] = useState('');
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [createdAdSetId, setCreatedAdSetId] = useState(null);

  // ── P1 additions: budget type, bid strategy, languages, audiences ──
  const [budgetType, setBudgetType] = useState('daily'); // 'daily' | 'lifetime'
  const [languageQuery, setLanguageQuery] = useState('');
  const [languageOptions, setLanguageOptions] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [customAudiences, setCustomAudiences] = useState([]);
  const [selectedAudiences, setSelectedAudiences] = useState([]);
  const [excludedAudiences, setExcludedAudiences] = useState([]);
  const [advantageAudience, setAdvantageAudience] = useState(false);
  const [scheduleDays, setScheduleDays] = useState([1, 2, 3, 4, 5]); // Mon-Fri default when scheduling is turned on
  const [scheduleAllHours, setScheduleAllHours] = useState(true);
  const [scheduleStartHour, setScheduleStartHour] = useState(9);
  const [scheduleEndHour, setScheduleEndHour] = useState(18);
  const [useAdScheduling, setUseAdScheduling] = useState(false);

  const [creative, setCreative] = useState({ pageId: '', headline: '', body: '', description: '', linkUrl: '', callToAction: 'LEARN_MORE', callPhone: '', leadGenFormId: '', format: 'single_image' });
  const [carouselCards, setCarouselCards] = useState([{ headline: '', description: '', linkUrl: '', imageHash: null, imageFile: null, imagePreviewUrl: null, uploading: false }]);
  const isLeadCampaign = campaign.objective === 'OUTCOME_LEADS';
  // Click-to-WhatsApp is an Engagement campaign whose ads open a WhatsApp chat.
  const [whatsappAd, setWhatsappAd] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const isWhatsappAd = whatsappAd && campaign.objective === 'OUTCOME_ENGAGEMENT';
  const [leadForms, setLeadForms] = useState([]);
  const [leadFormsLoading, setLeadFormsLoading] = useState(false);
  const [leadFormsError, setLeadFormsError] = useState(null);
  const [leadFormsReloadKey, setLeadFormsReloadKey] = useState(0);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({
    name: '', privacyPolicyUrl: '', websiteUrl: '',
    standardFields: ['FULL_NAME', 'PHONE', 'EMAIL'],
    customQuestions: [], // [{ key, label }]
    introHeadline: '', introDescription: '',
  });
  const [newQuestionLabel, setNewQuestionLabel] = useState('');
  const [creatingForm, setCreatingForm] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoId, setVideoId] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [conversionEvent, setConversionEvent] = useState('PURCHASE');
  const [imageHash, setImageHash] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewFormat, setPreviewFormat] = useState('MOBILE_FEED_STANDARD');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imageDims, setImageDims] = useState(null); // { width, height }

  const selectedCurrency = adAccounts.find((a) => a.id === campaign.adAccountId)?.currency || 'USD';
  const currencySymbol = getCurrencySymbol(selectedCurrency);

  useEffect(() => {
    dispatch(fetchAdAccounts());
    dispatch(fetchPages());
  }, [dispatch]);

  useEffect(() => () => { if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl); }, [imagePreviewUrl]);
  useEffect(() => () => carouselCards.forEach((c) => c.imagePreviewUrl && URL.revokeObjectURL(c.imagePreviewUrl)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const searchInterestsDebounced = async (q) => {
    setInterestQuery(q);
    if (!q || q.length < 2) return setInterestOptions([]);
    const result = await dispatch(searchInterests(q));
    if (!result.error) setInterestOptions(result.payload || []);
  };

  const searchLocationsDebounced = async (q) => {
    setLocationQuery(q);
    if (!q || q.length < 2) return setLocationOptions([]);
    const result = await dispatch(searchLocations(q));
    if (!result.error) setLocationOptions(result.payload || []);
  };

  const searchLanguagesDebounced = async (q) => {
    setLanguageQuery(q);
    if (!q || q.length < 2) return setLanguageOptions([]);
    const result = await dispatch(searchLanguages(q));
    if (!result.error) setLanguageOptions(result.payload || []);
  };

  // Custom/Lookalike audiences are per-ad-account, not a search — load the
  // whole (small) list once the account is known, same as the Page list.
  useEffect(() => {
    if (step !== 1 || !campaign.adAccountId) return;
    (async () => {
      const result = await dispatch(fetchCustomAudiences(campaign.adAccountId));
      if (!result.error) setCustomAudiences(result.payload || []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, campaign.adAccountId]);

  // One create at a time, and never a second campaign/ad set/ad for the same wizard run: going Back
  // and pressing the button again reuses what Meta already has instead of creating a duplicate.
  const submitting = useRef(false);

  const submitCampaign = async () => {
    if (submitting.current) return;
    if (createdCampaignId) {
      setAdSet((a) => ({ ...a, name: a.name || `${campaign.name} - Ad Set 1` }));
      return setStep(1);
    }
    submitting.current = true;
    setSaving(true);
    setError(null);
    const result = await dispatch(createCampaign({ adAccountId: campaign.adAccountId, campaignData: campaign }));
    submitting.current = false;
    setSaving(false);
    if (result.error) return setError(result.payload || 'Failed to create campaign');
    setCreatedCampaignId(result.payload._id);
    setAdSet((a) => ({ ...a, name: `${campaign.name} - Ad Set 1` }));
    setStep(1);
  };

  // A location search result can be a country, region, or city — each needs
  // its own geo_locations key on Meta's side. Previously this always built
  // `{ countries: [...] }` regardless of what was actually picked, so
  // selecting a city like "Mumbai" silently targeted the whole country
  // instead (confirmed live — a city's `key` isn't a country code).
  // Multiple picks are grouped by type into the single geo_locations object
  // Meta expects (countries/regions/cities arrays), not one object per pick.
  const buildGeoLocations = (locations) => {
    if (!locations || locations.length === 0) return undefined;
    const geo = {};
    for (const l of locations) {
      if (l.type === 'country') (geo.countries ||= []).push(l.key);
      else if (l.type === 'region') (geo.regions ||= []).push({ key: l.key });
      else {
        const unit = l.distance_unit === 'mile' ? 'mile' : 'kilometer';
        const min = unit === 'mile' ? 10 : 17;
        const max = unit === 'mile' ? 50 : 80;
        const r = Number(l.radius);
        const radius = Number.isFinite(r) ? Math.min(Math.max(r, min), max) : (unit === 'mile' ? 25 : 40);
        (geo.cities ||= []).push({ key: l.key, radius, distance_unit: unit });
      }
    }
    return geo;
  };

  const [reachEstimate, setReachEstimate] = useState(null);
  const [reachLoading, setReachLoading] = useState(false);

  // Debounced — fires whenever the audience selections actually change, so
  // the wizard gives feedback on reach before the ad set is created instead
  // of leaving the user targeting completely blind.
  useEffect(() => {
    if (step !== 1 || !campaign.adAccountId) return;
    const geoLocations = buildGeoLocations(selectedLocations);
    if (!geoLocations && selectedInterests.length === 0) { setReachEstimate(null); return; }
    const optimizationGoal = OPTIMIZATION_GOAL_BY_OBJECTIVE[campaign.objective] || 'REACH';
    const handle = setTimeout(async () => {
      setReachLoading(true);
      const result = await dispatch(fetchReachEstimate({
        adAccountId: campaign.adAccountId,
        optimizationGoal,
        ageMin: adSet.ageMin,
        ageMax: adSet.ageMax,
        interests: selectedInterests,
        geoLocations,
      }));
      setReachLoading(false);
      if (!result.error) setReachEstimate(result.payload);
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, adSet.ageMin, adSet.ageMax, selectedInterests, selectedLocations]);

  // Lead ads open a Page's Instant Form, so the form list is per Page.
  useEffect(() => {
    if (step !== 2 || !isLeadCampaign || !creative.pageId) return;
    let cancelled = false;
    (async () => {
      setLeadFormsLoading(true);
      setLeadFormsError(null);
      try {
        const { data } = await api.get(`/marketing/meta-ads/pages/${creative.pageId}/lead-forms`);
        const forms = (data.data?.forms || []).filter((f) => f.status !== 'ARCHIVED');
        if (cancelled) return;
        setLeadForms(forms);
        // A Page with no forms can't run a lead ad until one exists, so open the
        // create-form section instead of leaving it as an easy-to-miss link.
        if (forms.length === 0) setShowNewForm(true);
      } catch (e) {
        if (!cancelled) setLeadFormsError(e.response?.data?.error || e.response?.data?.message || 'Could not load this Page\'s Instant Forms');
      } finally {
        if (!cancelled) setLeadFormsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [step, isLeadCampaign, creative.pageId, leadFormsReloadKey]);

  // A button picked for one kind of ad (e.g. Shop Now) isn't offered on the
  // other — move to the first valid one rather than keep a hidden value.
  useEffect(() => {
    const options = isWhatsappAd ? CTA_OPTIONS_WHATSAPP : isLeadCampaign ? CTA_OPTIONS_LEAD : CTA_OPTIONS_LINK;
    if (!options.some((o) => o.value === creative.callToAction)) {
      setCreative((c) => ({ ...c, callToAction: options[0].value }));
    }
  }, [isLeadCampaign, isWhatsappAd, creative.callToAction]);

  const createLeadForm = async () => {
    setCreatingForm(true);
    setError(null);
    try {
      const questions = [
        ...newForm.standardFields,
        ...newForm.customQuestions.map((q) => ({ type: 'CUSTOM', key: q.key, label: q.label })),
      ];
      const { data } = await api.post(`/marketing/meta-ads/pages/${creative.pageId}/lead-forms`, { ...newForm, questions });
      const form = { id: data.data.form.id, name: newForm.name, status: 'ACTIVE', leadsCount: 0 };
      setLeadForms((prev) => [form, ...prev]);
      setCreative((c) => ({ ...c, leadGenFormId: form.id, linkUrl: c.linkUrl || (isExternalHttpUrl(newForm.websiteUrl) ? newForm.websiteUrl : '') }));
      setShowNewForm(false);
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || 'Failed to create the lead form');
    } finally {
      setCreatingForm(false);
    }
  };

  const togglePlacement = (id) => setPlacements((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));

  const buildAdsetSchedule = () => {
    if (!useAdScheduling || budgetType !== 'lifetime') return [];
    return [{
      days: scheduleDays,
      startMinute: scheduleAllHours ? 0 : scheduleStartHour * 60,
      endMinute: scheduleAllHours ? 1439 : scheduleEndHour * 60,
    }];
  };

  const submitAdSet = async () => {
    if (submitting.current) return;
    if (createdAdSetId) return setStep(2);
    submitting.current = true;
    setSaving(true);
    setError(null);
    const targeting = {
      ageMin: adSet.ageMin,
      ageMax: adSet.ageMax,
      genders: adSet.genders,
      interests: selectedInterests,
      geoLocations: buildGeoLocations(selectedLocations),
      locales: selectedLanguages.map((l) => l.key),
      customAudiences: selectedAudiences,
      excludedCustomAudiences: excludedAudiences,
      advantageAudience,
    };
    // Guard against past or imminent startTime
    let safeStartTime = adSet.startTime;
    if (safeStartTime) {
      const sDate = new Date(safeStartTime);
      const now = new Date();
      if (sDate.getTime() <= now.getTime() + 60000) {
        safeStartTime = undefined; // Start immediately
      }
    }

    const optimizationGoal = isWhatsappAd
      ? 'CONVERSATIONS'
      : (adSet.optimizationGoal || OPTIMIZATION_GOAL_BY_OBJECTIVE[campaign.objective] || 'LEAD_GENERATION');

    const destinationType = isWhatsappAd
      ? 'WHATSAPP'
      : (optimizationGoal === 'LEAD_GENERATION' || optimizationGoal === 'QUALITY_LEAD')
        ? 'ON_AD'
        : (optimizationGoal === 'OFFSITE_CONVERSIONS' || optimizationGoal === 'LINK_CLICKS' || optimizationGoal === 'LANDING_PAGE_VIEWS')
          ? 'WEBSITE'
          : (campaign.objective === 'OUTCOME_APP_PROMOTION' ? 'APP' : undefined);

    const result = await dispatch(createAdSet({
      campaignId: createdCampaignId,
      adSetData: {
        ...adSet,
        dailyBudget: budgetType === 'daily' ? adSet.dailyBudget : undefined,
        lifetimeBudget: budgetType === 'lifetime' ? Number(adSet.lifetimeBudget) : undefined,
        bidAmount: adSet.bidAmount ? Number(adSet.bidAmount) : undefined,
        startTime: safeStartTime || undefined,
        endTime: adSet.endTime || undefined,
        publisherPlatforms: placements,
        targeting,
        optimizationGoal,
        destinationType: isWhatsappAd ? 'WHATSAPP' : undefined,
        whatsappPhone: isWhatsappAd ? whatsappPhone : undefined,
        conversionEvent: (campaign.objective === 'OUTCOME_SALES' || optimizationGoal === 'OFFSITE_CONVERSIONS') ? conversionEvent : undefined,
        pageId: creative.pageId,
        adsetSchedule: buildAdsetSchedule(),
      },
    }));
    submitting.current = false;
    setSaving(false);
    if (result.error) return setError(result.payload || 'Failed to create ad set');
    setCreatedAdSetId(result.payload._id);
    setStep(2);
  };

  const [productDescription, setProductDescription] = useState('');
  const [copyVariants, setCopyVariants] = useState([]);
  const [generatingCopy, setGeneratingCopy] = useState(false);

  const handleGenerateCopy = async () => {
    if (!productDescription.trim()) return;
    setGeneratingCopy(true);
    setError(null);
    const result = await dispatch(generateAdCopy({ productDescription, objective: campaign.objective }));
    setGeneratingCopy(false);
    if (result.error) return setError(result.payload || 'Failed to generate ad copy');
    setCopyVariants(result.payload);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const check = await validateImageFile(file);
    if (!check.ok) return setError(check.error);
    setError(null);
    setImageFile(file);
    setImageDims(null);

    // A blob URL for the thumbnail preview, and its natural dimensions so a
    // low-resolution image can be flagged before it's uploaded — previously
    // only the raw filename showed, with no visual confirmation of what was
    // actually picked and no warning on an image too small for Meta's own
    // recommendations.
    const objectUrl = URL.createObjectURL(file);
    setImagePreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return objectUrl; });
    const img = new Image();
    img.onload = () => setImageDims({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = objectUrl;

    setUploadingImage(true);
    setError(null);
    const result = await dispatch(uploadCreativeImage({ campaignId: createdCampaignId, file }));
    setUploadingImage(false);
    if (result.error) return setError(result.payload || 'Failed to upload image');
    setImageHash(result.payload.imageHash);
  };

  const handleCarouselImageChange = async (index, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const check = await validateImageFile(file);
    if (!check.ok) return setError(`Card ${index + 1}: ${check.error}`);
    setError(null);
    const objectUrl = URL.createObjectURL(file);
    setCarouselCards((prev) => prev.map((c, i) => (i === index ? { ...c, imageFile: file, imagePreviewUrl: objectUrl, uploading: true } : c)));
    const result = await dispatch(uploadCreativeImage({ campaignId: createdCampaignId, file }));
    setCarouselCards((prev) => prev.map((c, i) => (i === index ? { ...c, uploading: false, imageHash: result.error ? null : result.payload.imageHash } : c)));
    if (result.error) setError(result.payload || 'Failed to upload carousel image');
  };

  const handleVideoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^video\/(mp4|quicktime)$/.test(file.type)) return setError('Use an MP4 or MOV video.');
    if (file.size > MAX_VIDEO_BYTES) return setError('That video is over 100 MB — compress it or use a shorter one.');
    setError(null);
    setVideoFile(file);
    setVideoId(null);
    setUploadingVideo(true);
    const result = await dispatch(uploadCreativeVideo({ campaignId: createdCampaignId, file }));
    setUploadingVideo(false);
    if (result.error) { setVideoFile(null); return setError(result.payload || 'Failed to upload video'); }
    setVideoId(result.payload.videoId);
  };

  const addCarouselCard = () => {
    if (carouselCards.length >= 10) return;
    setCarouselCards((prev) => [...prev, { headline: '', description: '', linkUrl: '', imageHash: null, imageFile: null, imagePreviewUrl: null, uploading: false }]);
  };
  const removeCarouselCard = (index) => setCarouselCards((prev) => prev.filter((_, i) => i !== index));
  const setCarouselCard = (index, patch) => setCarouselCards((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  const fetchPreview = async (format) => {
    setPreviewLoading(true);
    setError(null);
    try {
      const { data } = await api.post(`/marketing/meta-ads/campaigns/${createdCampaignId}/ads/preview`, {
        pageId: creative.pageId,
        adFormat: format,
        creative: {
          ...creative,
          imageHash,
          videoId: creative.format === 'video' ? videoId : undefined,
          leadGenFormId: isLeadCampaign ? creative.leadGenFormId : undefined,
          whatsapp: isWhatsappAd || undefined,
          carouselCards: creative.format === 'carousel'
            ? carouselCards.map((c) => ({ imageHash: c.imageHash, headline: c.headline, description: c.description, linkUrl: c.linkUrl }))
            : undefined,
        },
      });
      setPreviewHtml(data.data.previewHtml);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not render a preview for this ad.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const openPreview = () => fetchPreview(previewFormat);

  const submitAd = async () => {
    if (submitting.current) return;
    submitting.current = true;
    setSaving(true);
    setError(null);
    const result = await dispatch(createAd({
      campaignId: createdCampaignId,
      adData: {
        name: `${campaign.name} - Ad 1`,
        adSetId: createdAdSetId,
        pageId: creative.pageId,
        creative: {
          ...creative,
          imageHash,
          videoId: creative.format === 'video' ? videoId : undefined,
          leadGenFormId: isLeadCampaign ? creative.leadGenFormId : undefined,
          whatsapp: isWhatsappAd || undefined,
          carouselCards: creative.format === 'carousel'
            ? carouselCards.map((c) => ({ imageHash: c.imageHash, headline: c.headline, description: c.description, linkUrl: c.linkUrl }))
            : undefined,
        },
      },
    }));
    submitting.current = false;
    setSaving(false);
    if (result.error) return setError(result.payload || 'Failed to create ad');
    onDone?.();
  };

  // What the final step is still waiting for — shown as a checklist so a greyed-out
  // "Create Ad & Finish" always says why.
  const privacyUrlOk = isValidHttpUrl(newForm.privacyPolicyUrl);
  const newFormMissing = [
    !newForm.name.trim() && 'a form name',
    !privacyUrlOk && 'a privacy policy link that starts with https://',
    newForm.standardFields.length + newForm.customQuestions.length === 0 && 'at least one question',
  ].filter(Boolean);
  const isCarousel = creative.format === 'carousel';
  const adRequirements = [
    isCarousel
      ? { ok: carouselCards.length >= 2 && carouselCards.every((c) => c.imageHash), label: 'At least 2 carousel cards, each with an uploaded image', target: 'ad-field-image' }
      : { ok: !!imageHash, label: creative.format === 'video' ? 'An uploaded cover image' : 'An uploaded image', target: 'ad-field-image' },
    ...(creative.format === 'video' ? [{ ok: !!videoId, label: 'An uploaded video', target: 'ad-field-video' }] : []),
    ...(isCarousel ? [] : [{ ok: !!creative.headline.trim(), label: 'A headline', target: 'ad-field-headline' }]),
    ...(isLeadCampaign ? [{ ok: isExternalHttpUrl(creative.linkUrl), label: 'Your website link (https://…, not a Facebook page) — Meta requires it for lead ads', target: 'ad-field-link' }] : []),
    isLeadCampaign
      ? { ok: !!creative.leadGenFormId, label: 'An Instant Form — choose one or create one above', target: 'ad-field-form' }
      : isWhatsappAd
        ? { ok: true, label: 'WhatsApp chat — no link or form needed' }
        : creative.callToAction === 'CALL_NOW'
          // A phone-call ad dials the number — a website link is optional (Meta falls back to the Page).
          ? { ok: !creative.linkUrl || isValidHttpUrl(creative.linkUrl), label: 'Website link (optional) — if you add one it must start with https://', target: 'ad-field-link' }
          : { ok: isValidHttpUrl(creative.linkUrl), label: 'A destination link that starts with https://', target: 'ad-field-link' },
    ...(!isLeadCampaign && !isWhatsappAd && creative.callToAction === 'CALL_NOW' ? [{ ok: isValidPhone(creative.callPhone), label: 'A phone number for the Call Now button', target: 'ad-field-phone' }] : []),
  ];
  const adBlocked = adRequirements.some((r) => !r.ok);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-lg sm:rounded-lg w-full max-w-xl max-h-[100dvh] overflow-y-auto shadow-[0_2px_8px_rgba(0,0,0,0.16)]">
        <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                  i < step ? 'bg-[#E3F3E8] text-[#036B3A]' : i === step ? 'bg-[#0176D3] text-white' : 'bg-[#F4F6F9] text-[#5C5C5C]'
                }`}>
                  {i < step ? <Check size={12} /> : i + 1}
                </div>
                <span className={`text-[12px] font-semibold ${i === step ? 'text-[#2E2E2E]' : 'text-[#5C5C5C]'}`}>{s}</span>
                {i < STEPS.length - 1 && <ChevronRight size={14} className="text-[#D8DDE6]" />}
              </div>
            ))}
          </div>
          <button onClick={onClose} className="text-[#5C5C5C] hover:text-[#2E2E2E]"><X size={18} /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {error && <div className="bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-3 py-2 text-[12px] text-[#8E030F]">{error}</div>}

          {step === 0 && (
            <>
              <div>
                <label className={LABEL}>Ad Account</label>
                <select className={INPUT} value={campaign.adAccountId} onChange={(e) => setCampaign((c) => ({ ...c, adAccountId: e.target.value }))}>
                  <option value="">Select an ad account…</option>
                  {adAccounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL}>Campaign Name</label>
                <input className={INPUT} value={campaign.name} onChange={(e) => setCampaign((c) => ({ ...c, name: e.target.value }))} placeholder="Summer Lead Gen" />
              </div>
              <div>
                <label className={LABEL}>Objective</label>
                <select className={INPUT} value={campaign.objective} onChange={(e) => setCampaign((c) => ({ ...c, objective: e.target.value }))}>
                  <option value="OUTCOME_LEADS">Lead Generation</option>
                  <option value="OUTCOME_TRAFFIC">Traffic</option>
                  <option value="OUTCOME_ENGAGEMENT">Engagement</option>
                  <option value="OUTCOME_SALES">Sales (website conversions)</option>
                  <option value="OUTCOME_APP_PROMOTION">App Promotion</option>
                  <option value="OUTCOME_AWARENESS">Awareness</option>
                </select>
              </div>
              {campaign.objective === 'OUTCOME_ENGAGEMENT' && (
                <div className="border border-[#D8DDE6] rounded-[4px] p-3 space-y-2">
                  <label className={LABEL}>Where should people go when they tap the ad?</label>
                  <select className={INPUT} value={whatsappAd ? 'whatsapp' : 'default'} onChange={(e) => setWhatsappAd(e.target.value === 'whatsapp')}>
                    <option value="default">Stay on Facebook / Instagram (post engagement)</option>
                    <option value="whatsapp">Open a WhatsApp chat (Click to WhatsApp)</option>
                  </select>
                  {whatsappAd && (
                    <div>
                      <label className={LABEL}>WhatsApp number people will message (optional)</label>
                      <input className={INPUT} value={whatsappPhone} onChange={(e) => setWhatsappPhone(e.target.value)} placeholder="+971 50 123 4567" inputMode="tel" />
                      {whatsappPhone && !isValidPhone(whatsappPhone) ? (
                        <p className="text-[11px] text-[#8E030F] mt-1">Use digits only, starting with the country code, e.g. +971…</p>
                      ) : (
                        <p className="text-[11px] text-[#5C5C5C] mt-1">Leave blank to use the number linked to your Page. The Page you pick next must have a WhatsApp number linked in your WhatsApp Business account, or Meta will reject the ad.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className={LABEL}>Special Ad Category</label>
                <select
                  className={INPUT}
                  value={campaign.specialAdCategories[0] || 'NONE'}
                  onChange={(e) => setCampaign((c) => ({ ...c, specialAdCategories: e.target.value === 'NONE' ? [] : [e.target.value] }))}
                >
                  {SPECIAL_AD_CATEGORIES.map((cat) => <option key={cat.id} value={cat.id}>{cat.label}</option>)}
                </select>
                {campaign.specialAdCategories.length > 0 && (
                  <div className="mt-2">
                    <label className={LABEL}>Country this category applies to</label>
                    <input
                      className={INPUT}
                      value={campaign.specialAdCategoryCountry}
                      onChange={(e) => setCampaign((c) => ({ ...c, specialAdCategoryCountry: e.target.value.toUpperCase() }))}
                      placeholder="IN, US (comma-separated 2-letter codes)"
                    />
                  </div>
                )}
                <p className="text-[11px] text-[#5C5C5C] mt-1">Meta requires this to be declared truthfully — an undeclared ad in one of these areas gets rejected, or can restrict your ad account.</p>
              </div>

              <div>
                <label className={LABEL}>Budget Management</label>
                <div className="flex rounded-[4px] border border-[#D8DDE6] overflow-hidden w-fit">
                  <button type="button" onClick={() => setCampaign((c) => ({ ...c, cbo: false }))} className={`text-[12px] font-semibold px-3 py-1.5 ${!campaign.cbo ? 'bg-[#0176D3] text-white' : 'bg-white text-[#2E2E2E]'}`}>Ad Set Level</button>
                  <button type="button" onClick={() => setCampaign((c) => ({ ...c, cbo: true }))} className={`text-[12px] font-semibold px-3 py-1.5 ${campaign.cbo ? 'bg-[#0176D3] text-white' : 'bg-white text-[#2E2E2E]'}`}>Campaign Level (CBO)</button>
                </div>
                <p className="text-[11px] text-[#5C5C5C] mt-1">
                  {campaign.cbo
                    ? 'One shared budget for the whole campaign — Meta automatically shifts spend toward whichever ad set is performing best.'
                    : 'Each ad set gets its own fixed budget, set in the next step (this app\'s default).'}
                </p>
              </div>

              {campaign.cbo && (
                <>
                  <div>
                    <div className="flex rounded-[4px] border border-[#D8DDE6] overflow-hidden w-fit mb-2">
                      <button type="button" onClick={() => setCboBudgetType('daily')} className={`text-[12px] font-semibold px-3 py-1.5 ${cboBudgetType === 'daily' ? 'bg-[#0176D3] text-white' : 'bg-white text-[#2E2E2E]'}`}>Daily</button>
                      <button type="button" onClick={() => setCboBudgetType('lifetime')} className={`text-[12px] font-semibold px-3 py-1.5 ${cboBudgetType === 'lifetime' ? 'bg-[#0176D3] text-white' : 'bg-white text-[#2E2E2E]'}`}>Lifetime</button>
                    </div>
                    {cboBudgetType === 'daily' ? (
                      <div>
                        <label className={LABEL}>Campaign Daily Budget ({selectedCurrency})</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#5C5C5C]">{currencySymbol}</span>
                          <input type="number" min="0" step="0.01" className={`${INPUT} pl-7`} value={campaign.dailyBudget} onChange={(e) => setCampaign((c) => ({ ...c, dailyBudget: Number(e.target.value), lifetimeBudget: '' }))} />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className={LABEL}>Campaign Lifetime Budget ({selectedCurrency})</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#5C5C5C]">{currencySymbol}</span>
                            <input type="number" min="0" step="0.01" className={`${INPUT} pl-7`} value={campaign.lifetimeBudget} onChange={(e) => setCampaign((c) => ({ ...c, lifetimeBudget: Number(e.target.value), dailyBudget: '' }))} />
                          </div>
                        </div>
                        <div className="mt-2">
                          <label className={LABEL}>End Date</label>
                          <input type="datetime-local" className={INPUT} value={campaign.endTime} onChange={(e) => setCampaign((c) => ({ ...c, endTime: e.target.value }))} />
                        </div>
                      </>
                    )}
                  </div>
                  <div>
                    <label className={LABEL}>Bid Strategy</label>
                    <select
                      className={INPUT}
                      value={campaign.bidStrategy}
                      onChange={(e) => setCampaign((c) => ({ ...c, bidStrategy: e.target.value, bidAmount: e.target.value === 'LOWEST_COST_WITHOUT_CAP' ? '' : c.bidAmount }))}
                    >
                      <option value="LOWEST_COST_WITHOUT_CAP">Highest volume (let Meta spend freely for the most results)</option>
                      <option value="COST_CAP">Cost per result goal (keep average cost near a target)</option>
                      <option value="LOWEST_COST_WITH_BID_CAP">Bid cap (never bid above a set amount)</option>
                    </select>
                    {campaign.bidStrategy !== 'LOWEST_COST_WITHOUT_CAP' && (
                      <div className="relative mt-2">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#5C5C5C]">{currencySymbol}</span>
                        <input type="number" min="0" step="0.01" className={`${INPUT} pl-7`} value={campaign.bidAmount} onChange={(e) => setCampaign((c) => ({ ...c, bidAmount: Number(e.target.value) }))} placeholder={campaign.bidStrategy === 'COST_CAP' ? 'Target cost per result' : 'Maximum bid'} />
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {step === 1 && (
            <>
              <div>
                <label className={LABEL}>Ad Set Name</label>
                <input className={INPUT} value={adSet.name} onChange={(e) => setAdSet((a) => ({ ...a, name: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL}>Min Age</label>
                  <input type="number" className={INPUT} value={adSet.ageMin} onChange={(e) => setAdSet((a) => ({ ...a, ageMin: Number(e.target.value) }))} />
                </div>
                <div>
                  <label className={LABEL}>Max Age</label>
                  <input type="number" className={INPUT} value={adSet.ageMax} onChange={(e) => setAdSet((a) => ({ ...a, ageMax: Number(e.target.value) }))} />
                </div>
              </div>
              {campaign.cbo ? (
                <div className="bg-[#F4F6F9] border border-[#D8DDE6] rounded-[4px] p-3">
                  <p className="text-[12px] font-semibold text-[#2E2E2E]">Budget managed at the campaign level (CBO)</p>
                  <p className="text-[11px] text-[#5C5C5C] mt-1">
                    This ad set has no budget of its own — {currencySymbol}{campaign.lifetimeBudget || campaign.dailyBudget || 0}{campaign.lifetimeBudget ? ' lifetime' : '/day'} is shared across every ad set in this campaign, set in the previous step.
                  </p>
                </div>
              ) : (
                <>
              <div>
                <label className={LABEL}>Budget Type</label>
                <div className="flex rounded-[4px] border border-[#D8DDE6] overflow-hidden w-fit">
                  <button type="button" onClick={() => setBudgetType('daily')} className={`text-[12px] font-semibold px-3 py-1.5 ${budgetType === 'daily' ? 'bg-[#0176D3] text-white' : 'bg-white text-[#2E2E2E]'}`}>Daily</button>
                  <button type="button" onClick={() => setBudgetType('lifetime')} className={`text-[12px] font-semibold px-3 py-1.5 ${budgetType === 'lifetime' ? 'bg-[#0176D3] text-white' : 'bg-white text-[#2E2E2E]'}`}>Lifetime</button>
                </div>
              </div>
              {budgetType === 'daily' ? (
                <div>
                  <label className={LABEL}>Daily Budget ({selectedCurrency})</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#5C5C5C]">{currencySymbol}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={`${INPUT} pl-7`}
                      value={adSet.dailyBudget}
                      onChange={(e) => setAdSet((a) => ({ ...a, dailyBudget: Number(e.target.value) }))}
                    />
                  </div>
                  <p className="text-[11px] text-[#5C5C5C] mt-1">How much to spend per day, in your ad account's own currency.</p>
                  {adSet.dailyBudget > 0 && adSet.dailyBudget < (LOW_BUDGET_FLOOR[selectedCurrency] || 5) && (
                    <p className="text-[11px] text-[#8C4B02] bg-[#FFF4E5] border border-[#FFB75D] rounded-[4px] px-2 py-1 mt-1">
                      This is very low for {selectedCurrency} — Meta may reject it outright, or it may run so slowly it barely delivers any leads. Consider at least {currencySymbol}{LOW_BUDGET_FLOOR[selectedCurrency] || 5}/day.
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <label className={LABEL}>Lifetime Budget ({selectedCurrency})</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#5C5C5C]">{currencySymbol}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={`${INPUT} pl-7`}
                      value={adSet.lifetimeBudget}
                      onChange={(e) => setAdSet((a) => ({ ...a, lifetimeBudget: e.target.value }))}
                    />
                  </div>
                  <p className="text-[11px] text-[#5C5C5C] mt-1">Total to spend across the whole run — requires an end date below, which Meta uses to pace spending.</p>
                </div>
              )}
              <div>
                <label className={LABEL}>Bid Strategy</label>
                <select
                  className={INPUT}
                  value={adSet.bidStrategy}
                  onChange={(e) => setAdSet((a) => ({ ...a, bidStrategy: e.target.value, bidAmount: e.target.value === 'LOWEST_COST_WITHOUT_CAP' ? '' : a.bidAmount }))}
                >
                  <option value="LOWEST_COST_WITHOUT_CAP">Highest volume (let Meta spend freely for the most results)</option>
                  <option value="COST_CAP">Cost per result goal (keep average cost near a target)</option>
                  <option value="LOWEST_COST_WITH_BID_CAP">Bid cap (never bid above a set amount)</option>
                </select>
                {adSet.bidStrategy !== 'LOWEST_COST_WITHOUT_CAP' && (
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#5C5C5C]">{currencySymbol}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={`${INPUT} pl-7`}
                      placeholder={adSet.bidStrategy === 'COST_CAP' ? 'Target cost per result' : 'Maximum bid'}
                      value={adSet.bidAmount}
                      onChange={(e) => setAdSet((a) => ({ ...a, bidAmount: e.target.value }))}
                    />
                  </div>
                )}
              </div>
                </>
              )}
              {/* Dynamic Performance Goals per Objective */}
              <div>
                <label className={LABEL}>Performance Goal</label>
                {campaign.objective === 'OUTCOME_LEADS' && (
                  <select className={INPUT} value={adSet.optimizationGoal} onChange={(e) => setAdSet((a) => ({ ...a, optimizationGoal: e.target.value }))}>
                    <option value="LEAD_GENERATION">Instant Forms: Maximise leads (most leads for budget)</option>
                    <option value="QUALITY_LEAD">Instant Forms: Maximise conversion leads (higher intent)</option>
                    <option value="OFFSITE_CONVERSIONS">Website: Maximise conversions (tracked with Pixel)</option>
                    <option value="CONVERSATIONS">WhatsApp: Maximise conversations (start chat)</option>
                  </select>
                )}
                {campaign.objective === 'OUTCOME_TRAFFIC' && (
                  <select className={INPUT} value={adSet.optimizationGoal} onChange={(e) => setAdSet((a) => ({ ...a, optimizationGoal: e.target.value }))}>
                    <option value="LINK_CLICKS">Maximise link clicks</option>
                    <option value="LANDING_PAGE_VIEWS">Maximise landing page views</option>
                  </select>
                )}
                {campaign.objective === 'OUTCOME_ENGAGEMENT' && (
                  <select className={INPUT} value={adSet.optimizationGoal} onChange={(e) => setAdSet((a) => ({ ...a, optimizationGoal: e.target.value }))}>
                    <option value="POST_ENGAGEMENT">Maximise post engagement</option>
                    <option value="PAGE_LIKES">Maximise page likes</option>
                    <option value="CONVERSATIONS">Maximise conversations (WhatsApp/Messenger)</option>
                  </select>
                )}
                {campaign.objective === 'OUTCOME_SALES' && (
                  <select className={INPUT} value={adSet.optimizationGoal} onChange={(e) => setAdSet((a) => ({ ...a, optimizationGoal: e.target.value }))}>
                    <option value="OFFSITE_CONVERSIONS">Website: Maximise conversions (Purchase, Add to cart)</option>
                    <option value="CONVERSATIONS">WhatsApp: Maximise purchase conversations</option>
                  </select>
                )}
                {campaign.objective === 'OUTCOME_APP_PROMOTION' && (
                  <select className={INPUT} value={adSet.optimizationGoal} onChange={(e) => setAdSet((a) => ({ ...a, optimizationGoal: e.target.value }))}>
                    <option value="APP_INSTALLS">Maximise app installs</option>
                    <option value="VALUE">Maximise value of app events</option>
                  </select>
                )}
                {campaign.objective === 'OUTCOME_AWARENESS' && (
                  <select className={INPUT} value={adSet.optimizationGoal} onChange={(e) => setAdSet((a) => ({ ...a, optimizationGoal: e.target.value }))}>
                    <option value="REACH">Maximise reach of ads</option>
                    <option value="IMPRESSIONS">Maximise number of impressions</option>
                    <option value="AD_RECALL_LIFT">Maximise ad recall lift</option>
                  </select>
                )}
              </div>

              {(campaign.objective === 'OUTCOME_SALES' || adSet.optimizationGoal === 'OFFSITE_CONVERSIONS') && (
                <div>
                  <label className={LABEL}>Conversion Event</label>
                  <select className={INPUT} value={conversionEvent} onChange={(e) => setConversionEvent(e.target.value)}>
                    {CONVERSION_EVENT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  <p className="text-[11px] text-[#5C5C5C] mt-1">Meta finds people likely to do this on your website. It uses the Pixel ID saved for this ad account in Meta Ads Settings — add it there first.</p>
                </div>
              )}

              <div>
                <label className={LABEL}>Gender</label>
                <div className="flex gap-2">
                  {[{ id: 0, label: 'All' }, { id: 1, label: 'Men' }, { id: 2, label: 'Women' }].map((g) => {
                    const active = g.id === 0 ? adSet.genders.length === 0 : adSet.genders.includes(g.id);
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => setAdSet((a) => ({ ...a, genders: g.id === 0 ? [] : [g.id] }))}
                        className={`text-[12px] px-3 py-1.5 rounded-full border  whitespace-nowrap ${active ? 'bg-[#0176D3] border-[#0176D3] text-white' : 'border-[#D8DDE6] text-[#2E2E2E]'}`}
                      >
                        {g.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className={LABEL}>Location</label>
                <input className={INPUT} value={locationQuery} onChange={(e) => searchLocationsDebounced(e.target.value)} placeholder="Search a country/city…" />
                {locationOptions.length > 0 && (
                  <div className="border border-[#D8DDE6] rounded-[4px] mt-1 max-h-32 overflow-auto">
                    {locationOptions.map((l) => (
                      <button
                        key={`${l.type}-${l.key}`}
                        onClick={() => {
                          if (!selectedLocations.find((s) => s.key === l.key && s.type === l.type)) {
                            const newLoc = { ...l };
                            if (l.type === 'city') {
                              newLoc.radius = 25;
                              newLoc.distance_unit = 'kilometer';
                            }
                            setSelectedLocations((prev) => [...prev, newLoc]);
                          }
                          setLocationQuery('');
                          setLocationOptions([]);
                        }}
                        className="block w-full text-left px-3 py-2 text-[12px] hover:bg-[#F4F6F9]"
                      >
                        {l.name} <span className="text-[#5C5C5C]">({l.type})</span>
                      </button>
                    ))}
                  </div>
                )}

                {selectedLocations.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {selectedLocations.map((l) => (
                      <div key={`${l.type}-${l.key}`} className="bg-[#F8F9FB] border border-[#D8DDE6] rounded-[4px] p-2 text-[12px]">
                        <div className="flex items-center justify-between font-semibold text-[#2E2E2E]">
                          <span>{l.name} <span className="text-[#5C5C5C] font-normal">({l.type})</span></span>
                          <button onClick={() => setSelectedLocations((prev) => prev.filter((s) => !(s.key === l.key && s.type === l.type)))} className="text-[#5C5C5C] hover:text-[#8E030F]">
                            <X size={14} />
                          </button>
                        </div>
                        {l.type === 'city' && (
                          <div className="mt-2 flex flex-wrap items-center gap-3 pt-2 border-t border-[#E8ECEF]">
                            <span className="text-[11px] text-[#5C5C5C]">Radius:</span>
                            <input
                              type="range"
                              min={l.distance_unit === 'mile' ? 10 : 17}
                              max={l.distance_unit === 'mile' ? 50 : 80}
                              value={l.radius || (l.distance_unit === 'mile' ? 25 : 40)}
                              onChange={(e) => {
                                const val = Number(e.target.value);
                                setSelectedLocations((prev) => prev.map((s) => (s.key === l.key && s.type === l.type ? { ...s, radius: val } : s)));
                              }}
                              className="w-28 accent-[#0176D3]"
                            />
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min={l.distance_unit === 'mile' ? 10 : 17}
                                max={l.distance_unit === 'mile' ? 50 : 80}
                                value={l.radius || (l.distance_unit === 'mile' ? 25 : 40)}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  setSelectedLocations((prev) => prev.map((s) => (s.key === l.key && s.type === l.type ? { ...s, radius: val } : s)));
                                }}
                                className="w-14 px-1.5 py-0.5 border border-[#D8DDE6] rounded text-[11px]"
                              />
                              <div className="flex rounded border border-[#D8DDE6] overflow-hidden text-[10px] font-semibold">
                                <button
                                  type="button"
                                  onClick={() => setSelectedLocations((prev) => prev.map((s) => (s.key === l.key && s.type === l.type ? { ...s, distance_unit: 'kilometer', radius: Math.min(Math.max(Math.round((s.radius || 25) * 1.6), 17), 80) } : s)))}
                                  className={`px-1.5 py-0.5 ${l.distance_unit !== 'mile' ? 'bg-[#0176D3] text-white' : 'bg-white text-[#2E2E2E]'}`}
                                >
                                  km
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setSelectedLocations((prev) => prev.map((s) => (s.key === l.key && s.type === l.type ? { ...s, distance_unit: 'mile', radius: Math.min(Math.max(Math.round((s.radius || 40) / 1.6), 10), 50) } : s)))}
                                  className={`px-1.5 py-0.5 ${l.distance_unit === 'mile' ? 'bg-[#0176D3] text-white' : 'bg-white text-[#2E2E2E]'}`}
                                >
                                  mi
                                </button>
                              </div>
                            </div>
                            <span className="text-[10px] text-[#5C5C5C]">(Allowed: {l.distance_unit === 'mile' ? '10-50 mi' : '17-80 km'})</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className={LABEL}>Interests</label>
                <input className={INPUT} value={interestQuery} onChange={(e) => searchInterestsDebounced(e.target.value)} placeholder="Search interests…" />
                {interestOptions.length > 0 && (
                  <div className="border border-[#D8DDE6] rounded-[4px] mt-1 max-h-32 overflow-auto">
                    {interestOptions.map((i) => (
                      <button
                        key={i.id}
                        onClick={() => { if (!selectedInterests.find((s) => s.id === i.id)) setSelectedInterests((prev) => [...prev, i]); setInterestQuery(''); setInterestOptions([]); }}
                        className="block w-full text-left px-3 py-2 text-[12px] hover:bg-[#F4F6F9]"
                      >
                        {i.name}
                      </button>
                    ))}
                  </div>
                )}
                {selectedInterests.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedInterests.map((i) => (
                      <span key={i.id} className="bg-[#F4F6F9] border border-[#D8DDE6] rounded-full px-2 py-1 text-[11px] flex items-center gap-1">
                        {i.name}
                        <button onClick={() => setSelectedInterests((prev) => prev.filter((s) => s.id !== i.id))}><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className={LABEL}>Languages (optional)</label>
                <input className={INPUT} value={languageQuery} onChange={(e) => searchLanguagesDebounced(e.target.value)} placeholder="e.g. Hindi, English…" />
                {languageOptions.length > 0 && (
                  <div className="border border-[#D8DDE6] rounded-[4px] mt-1 max-h-32 overflow-auto">
                    {languageOptions.map((l) => (
                      <button
                        key={l.key}
                        onClick={() => { if (!selectedLanguages.find((s) => s.key === l.key)) setSelectedLanguages((prev) => [...prev, l]); setLanguageQuery(''); setLanguageOptions([]); }}
                        className="block w-full text-left px-3 py-2 text-[12px] hover:bg-[#F4F6F9]"
                      >
                        {l.name}
                      </button>
                    ))}
                  </div>
                )}
                {selectedLanguages.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedLanguages.map((l) => (
                      <span key={l.key} className="bg-[#F4F6F9] border border-[#D8DDE6] rounded-full px-2 py-1 text-[11px] flex items-center gap-1">
                        {l.name}
                        <button onClick={() => setSelectedLanguages((prev) => prev.filter((s) => s.key !== l.key))}><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-[#5C5C5C] mt-1">Leave blank to target every language.</p>
              </div>

              {customAudiences.length > 0 && (
                <div>
                  <label className={LABEL}>Custom & Lookalike Audiences</label>
                  <div className="border border-[#D8DDE6] rounded-[4px] max-h-40 overflow-auto divide-y divide-[#D8DDE6]">
                    {customAudiences.map((a) => {
                      const included = selectedAudiences.some((s) => s.id === a.id);
                      const excluded = excludedAudiences.some((s) => s.id === a.id);
                      return (
                        <div key={a.id} className="flex items-center justify-between px-3 py-2 text-[12px]">
                          <span className="truncate flex-1">{a.name}{a.isLookalike && <span className="ml-1 text-[10px] text-[#5C5C5C]">(Lookalike)</span>}</span>
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                setExcludedAudiences((prev) => prev.filter((s) => s.id !== a.id));
                                setSelectedAudiences((prev) => (included ? prev.filter((s) => s.id !== a.id) : [...prev, { id: a.id, name: a.name }]));
                              }}
                              className={`text-[10px] font-bold px-2 py-1 rounded ${included ? 'bg-[#2E844A] text-white' : 'bg-[#F4F6F9] text-[#2E2E2E]'}`}
                            >
                              Include
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedAudiences((prev) => prev.filter((s) => s.id !== a.id));
                                setExcludedAudiences((prev) => (excluded ? prev.filter((s) => s.id !== a.id) : [...prev, { id: a.id, name: a.name }]));
                              }}
                              className={`text-[10px] font-bold px-2 py-1 rounded ${excluded ? 'bg-[#BA0517] text-white' : 'bg-[#F4F6F9] text-[#2E2E2E]'}`}
                            >
                              Exclude
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-[#5C5C5C] mt-1">Exclude an audience you've already converted (e.g. existing customers) to stop paying for the same lead twice.</p>
                </div>
              )}

              <label className="flex items-center justify-between p-3 rounded-[4px] border border-[#D8DDE6] cursor-pointer">
                <div>
                  <span className="block text-[12px] font-semibold text-[#2E2E2E]">Advantage+ Audience</span>
                  <span className="block text-[11px] text-[#5C5C5C]">Let Meta expand beyond your picks when it finds better opportunities. Age and location stay as hard limits.</span>
                </div>
                <input type="checkbox" checked={advantageAudience} onChange={(e) => setAdvantageAudience(e.target.checked)} className="w-4 h-4" />
              </label>

              {budgetType === 'lifetime' && (
                <div className="border border-[#D8DDE6] rounded-[4px] p-3 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={useAdScheduling} onChange={(e) => setUseAdScheduling(e.target.checked)} className="w-4 h-4" />
                    <span className="text-[12px] font-semibold text-[#2E2E2E]">Only run on specific days/hours</span>
                  </label>
                  {useAdScheduling && (
                    <>
                      <div className="flex flex-wrap gap-1">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setScheduleDays((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]))}
                            className={`text-[11px] font-semibold px-2 py-1 rounded-full border  whitespace-nowrap ${scheduleDays.includes(i) ? 'bg-[#0176D3] border-[#0176D3] text-white' : 'border-[#D8DDE6] text-[#2E2E2E]'}`}
                          >
                            {d}
                          </button>
                        ))}
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={scheduleAllHours} onChange={(e) => setScheduleAllHours(e.target.checked)} className="w-4 h-4" />
                        <span className="text-[12px] text-[#2E2E2E]">All hours on those days</span>
                      </label>
                      {!scheduleAllHours && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className={LABEL}>From (hour)</label>
                            <input type="number" min="0" max="23" className={INPUT} value={scheduleStartHour} onChange={(e) => setScheduleStartHour(Number(e.target.value))} />
                          </div>
                          <div>
                            <label className={LABEL}>To (hour)</label>
                            <input type="number" min="0" max="23" className={INPUT} value={scheduleEndHour} onChange={(e) => setScheduleEndHour(Number(e.target.value))} />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className={LABEL} style={{ marginBottom: 0 }}>Start (optional)</label>
                    {adSet.startTime && (
                      <button
                        type="button"
                        onClick={() => setAdSet((a) => ({ ...a, startTime: '' }))}
                        className="text-[10px] text-[#0176D3] hover:underline"
                      >
                        Start Immediately
                      </button>
                    )}
                  </div>
                  <input type="datetime-local" className={INPUT} value={adSet.startTime} onChange={(e) => setAdSet((a) => ({ ...a, startTime: e.target.value }))} />
                </div>
                <div>
                  <label className={LABEL}>End {budgetType === 'lifetime' ? '(required)' : '(optional)'}</label>
                  <input type="datetime-local" className={INPUT} value={adSet.endTime} onChange={(e) => setAdSet((a) => ({ ...a, endTime: e.target.value }))} />
                </div>
              </div>
              <p className="text-[11px] text-[#5C5C5C] -mt-2">
                {budgetType === 'lifetime'
                  ? 'A lifetime budget needs an end date — Meta paces spending across the whole period.'
                  : 'Leave start blank to start immediately upon publishing. Any past start time is automatically adjusted.'}
              </p>

              <div>
                <label className={LABEL}>Placements</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setPlacements([])}
                    className={`text-[12px] px-3 py-1.5 rounded-full border  whitespace-nowrap ${placements.length === 0 ? 'bg-[#0176D3] border-[#0176D3] text-white' : 'border-[#D8DDE6] text-[#2E2E2E]'}`}
                  >
                    Automatic (recommended)
                  </button>
                  {PLACEMENTS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePlacement(p.id)}
                      className={`text-[12px] px-3 py-1.5 rounded-full border  whitespace-nowrap ${placements.includes(p.id) ? 'bg-[#0176D3] border-[#0176D3] text-white' : 'border-[#D8DDE6] text-[#2E2E2E]'}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-[#5C5C5C] mt-1">Automatic lets Meta show the ad wherever it performs best. Pick specific apps only if you need to.</p>
              </div>

              {(reachLoading || reachEstimate) && (
                <div className="bg-[#F4F6F9] border border-[#D8DDE6] rounded-[4px] px-3 py-2 text-[12px]">
                  {reachLoading ? (
                    <span className="text-[#5C5C5C]">Estimating audience size…</span>
                  ) : (
                    <span className="text-[#2E2E2E]">
                      <strong>Estimated audience:</strong>{' '}
                      {reachEstimate?.estimateMauLower != null
                        ? `${Number(reachEstimate.estimateMauLower).toLocaleString()} – ${Number(reachEstimate.estimateMauUpper).toLocaleString()} people`
                        : 'Not enough targeting selected yet to estimate.'}
                    </span>
                  )}
                </div>
              )}

              <div>
                <label className={LABEL}>Facebook Page (runs the ad)</label>
                <select className={INPUT} value={creative.pageId} onChange={(e) => setCreative((c) => ({ ...c, pageId: e.target.value }))}>
                  <option value="">Select a page…</option>
                  {pages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <label className={LABEL}>Ad Format</label>
                <div className="flex rounded-[4px] border border-[#D8DDE6] overflow-hidden w-fit">
                  <button type="button" onClick={() => setCreative((c) => ({ ...c, format: 'single_image' }))} className={`text-[12px] font-semibold px-3 py-1.5 ${creative.format === 'single_image' ? 'bg-[#0176D3] text-white' : 'bg-white text-[#2E2E2E]'}`}>Single Image</button>
                  <button type="button" onClick={() => setCreative((c) => ({ ...c, format: 'carousel' }))} className={`text-[12px] font-semibold px-3 py-1.5 ${creative.format === 'carousel' ? 'bg-[#0176D3] text-white' : 'bg-white text-[#2E2E2E]'}`}>Carousel</button>
                  <button type="button" onClick={() => setCreative((c) => ({ ...c, format: 'video' }))} className={`text-[12px] font-semibold px-3 py-1.5 ${creative.format === 'video' ? 'bg-[#0176D3] text-white' : 'bg-white text-[#2E2E2E]'}`}>Video</button>
                </div>
              </div>

              {creative.format === 'carousel' ? (
                <div className="space-y-2" id="ad-field-image">
                  <label className={LABEL}>Carousel Cards (2–10)</label>
                  {carouselCards.map((card, i) => (
                    <div key={i} className="border border-[#D8DDE6] rounded-[4px] p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-[#706E6B]">Card {i + 1}</span>
                        {carouselCards.length > 1 && (
                          <button type="button" onClick={() => removeCarouselCard(i)} className="text-[11px] text-[#BA0517] hover:underline">Remove</button>
                        )}
                      </div>
                      {card.imagePreviewUrl ? (
                        <div className="flex items-center gap-2">
                          <img src={card.imagePreviewUrl} alt={`Card ${i + 1} preview`} className="w-12 h-12 object-cover rounded-[4px] border border-[#D8DDE6]" />
                          <label className="text-[11px] font-semibold text-[#0176D3] hover:underline cursor-pointer">
                            {card.uploading ? 'Uploading…' : 'Replace'}
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleCarouselImageChange(i, e)} />
                          </label>
                        </div>
                      ) : (
                        <label className="flex items-center justify-center gap-2 border border-dashed border-[#D8DDE6] hover:border-[#0176D3] rounded-[4px] px-3 py-3 cursor-pointer text-[12px] text-[#5C5C5C]">
                          <Upload size={14} /> Upload image
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleCarouselImageChange(i, e)} />
                        </label>
                      )}
                      <input className={INPUT} placeholder="Card headline" value={card.headline} onChange={(e) => setCarouselCard(i, { headline: e.target.value })} />
                      <input className={INPUT} placeholder="Card description (optional)" value={card.description} onChange={(e) => setCarouselCard(i, { description: e.target.value })} />
                      <input className={INPUT} placeholder="Card link (optional — defaults to the ad's main link)" value={card.linkUrl} onChange={(e) => setCarouselCard(i, { linkUrl: e.target.value })} />
                    </div>
                  ))}
                  {carouselCards.length < 10 && (
                    <button type="button" onClick={addCarouselCard} className="text-[12px] font-semibold text-[#0176D3] hover:underline">+ Add another card</button>
                  )}
                </div>
              ) : (
              <>
              {creative.format === 'video' && (
                <div id="ad-field-video">
                  <label className={LABEL}>Ad Video</label>
                  {videoFile ? (
                    <div className="flex items-center gap-3 border border-[#D8DDE6] rounded-[4px] p-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-[#2E2E2E] truncate">{videoFile.name}</p>
                        <p className="text-[11px] text-[#5C5C5C]">{uploadingVideo ? 'Uploading to Meta — larger videos can take a few minutes…' : videoId ? 'Uploaded' : ''}</p>
                      </div>
                      <label className="text-[12px] font-semibold text-[#0176D3] hover:underline cursor-pointer flex-shrink-0">
                        Replace
                        <input type="file" accept="video/mp4,video/quicktime" className="hidden" onChange={handleVideoChange} disabled={uploadingVideo} />
                      </label>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 border border-dashed border-[#D8DDE6] hover:border-[#0176D3] rounded-[4px] px-4 py-6 cursor-pointer text-[13px] text-[#5C5C5C]">
                      <Upload size={16} />
                      Click to upload an MP4 or MOV video (up to 100 MB)
                      <input type="file" accept="video/mp4,video/quicktime" className="hidden" onChange={handleVideoChange} />
                    </label>
                  )}
                </div>
              )}
              <div id="ad-field-image">
                <label className={LABEL}>{creative.format === 'video' ? 'Cover Image (shown before the video plays)' : 'Ad Image'}</label>
                {imagePreviewUrl ? (
                  <div className="flex items-center gap-3 border border-[#D8DDE6] rounded-[4px] p-3">
                    <img src={imagePreviewUrl} alt="Ad creative preview" className="w-16 h-16 object-cover rounded-[4px] border border-[#D8DDE6]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-[#2E2E2E] truncate">{uploadingImage ? 'Uploading…' : imageFile?.name}</p>
                      {imageDims && (
                        <p className="text-[11px] text-[#5C5C5C]">
                          {imageDims.width}×{imageDims.height}px · {(imageDims.width / imageDims.height).toFixed(2)}:1
                        </p>
                      )}
                      {imageDims && imageDims.width < 1080 && (
                        <p className="text-[11px] text-[#8C4B02]">Below Meta's recommended 1080px width — the ad may look soft or blurry.</p>
                      )}
                    </div>
                    <label className="text-[12px] font-semibold text-[#0176D3] hover:underline cursor-pointer flex-shrink-0">
                      Replace
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                    </label>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 border border-dashed border-[#D8DDE6] hover:border-[#0176D3] rounded-[4px] px-4 py-6 cursor-pointer text-[13px] text-[#5C5C5C]">
                    <Upload size={16} />
                    Click to upload an image
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                  </label>
                )}
              </div>
              </>
              )}
              <div className="bg-[#F4F6F9] border border-dashed border-[#D8DDE6] rounded-[4px] p-3 space-y-2">
                <label className={LABEL}>Not sure what to write? Generate with AI</label>
                <div className="flex gap-2">
                  <input
                    className={`${INPUT} bg-white`}
                    value={productDescription}
                    onChange={(e) => setProductDescription(e.target.value)}
                    placeholder="One line about your product/service — e.g. 'affordable web design for small businesses'"
                  />
                  <button
                    onClick={handleGenerateCopy}
                    disabled={generatingCopy || !productDescription.trim()}
                    className="flex items-center gap-1.5 flex-shrink-0 bg-[#0176D3] hover:bg-[#0153A0] disabled:opacity-50 text-white text-[12px] font-semibold px-3 py-2 rounded-[4px] whitespace-nowrap"
                  >
                    <Sparkles size={13} /> {generatingCopy ? 'Generating…' : 'Generate'}
                  </button>
                </div>
                {copyVariants.length > 0 && (
                  <div className="space-y-1.5">
                    {copyVariants.map((v, i) => (
                      <button
                        key={i}
                        onClick={() => setCreative((c) => ({ ...c, headline: v.headline, body: v.body }))}
                        className="block w-full text-left bg-white border border-[#D8DDE6] hover:border-[#0176D3] rounded-[4px] px-3 py-2"
                      >
                        <p className="text-[12px] font-bold text-[#2E2E2E]">{v.headline}</p>
                        <p className="text-[11px] text-[#5C5C5C]">{v.body}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className={LABEL}>Headline</label>
                <input id="ad-field-headline" className={INPUT} value={creative.headline} onChange={(e) => setCreative((c) => ({ ...c, headline: e.target.value }))} placeholder="Get a free quote today" />
              </div>
              <div>
                <label className={LABEL}>Body Text</label>
                <textarea className={INPUT} rows={3} value={creative.body} onChange={(e) => setCreative((c) => ({ ...c, body: e.target.value }))} />
              </div>
              {creative.format !== 'carousel' && (
                <div>
                  <label className={LABEL}>Description (optional)</label>
                  <input className={INPUT} value={creative.description} onChange={(e) => setCreative((c) => ({ ...c, description: e.target.value }))} placeholder="A short line shown under the headline in Feed" />
                </div>
              )}
              {isLeadCampaign ? (
                <>
                <div className="border border-[#D8DDE6] rounded-[4px] p-3 space-y-2" id="ad-field-form">
                  <label className={LABEL}>Instant Form (where people submit their details)</label>
                  {leadFormsLoading ? (
                    <p className="text-[12px] text-[#5C5C5C]">Loading this Page's forms…</p>
                  ) : (
                    <select className={INPUT} value={creative.leadGenFormId} onChange={(e) => setCreative((c) => ({ ...c, leadGenFormId: e.target.value }))}>
                      <option value="">{leadForms.length ? 'Select a form…' : leadFormsError ? 'Forms unavailable — see the message below' : 'No forms on this Page yet — create one below'}</option>
                      {leadForms.map((f) => <option key={f.id} value={f.id}>{f.name} ({f.leadsCount} leads)</option>)}
                    </select>
                  )}
                  {leadFormsError && (
                    <div className="bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-3 py-2 text-[12px] text-[#8E030F] flex items-start justify-between gap-2">
                      <span>Couldn't load this Page's Instant Forms: {leadFormsError}</span>
                      <button type="button" onClick={() => setLeadFormsReloadKey((k) => k + 1)} className="font-semibold underline whitespace-nowrap">Retry</button>
                    </div>
                  )}
                  {!leadFormsLoading && !leadFormsError && leadForms.length === 0 && (
                    <p className="text-[12px] text-[#5C5C5C] bg-[#EAF4FE] border border-[#B8DAF3] rounded-[4px] px-3 py-2">
                      This Page has no Instant Forms yet. A lead ad needs one — fill in the form below to create it.
                    </p>
                  )}
                  {!showNewForm ? (
                    <button type="button" onClick={() => setShowNewForm(true)} className="text-[12px] font-semibold text-[#0176D3] hover:underline">
                      + Create a new form
                    </button>
                  ) : (
                    <div className="space-y-2 pt-1">
                      <input className={INPUT} value={newForm.name} onChange={(e) => setNewForm((f) => ({ ...f, name: e.target.value }))} placeholder="Form name, e.g. Free Quote Request" />

                      <div>
                        <label className="text-[11px] font-semibold text-[#706E6B]">Intro screen (optional)</label>
                        <input className={`${INPUT} mt-1`} value={newForm.introHeadline} onChange={(e) => setNewForm((f) => ({ ...f, introHeadline: e.target.value }))} placeholder="Headline shown before the questions" />
                        {newForm.introHeadline && (
                          <textarea className={`${INPUT} mt-1`} rows={2} value={newForm.introDescription} onChange={(e) => setNewForm((f) => ({ ...f, introDescription: e.target.value }))} placeholder="A line or two explaining what this form is for" />
                        )}
                      </div>

                      <div>
                        <label className="text-[11px] font-semibold text-[#706E6B]">Questions to ask</label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {STANDARD_FIELD_OPTIONS.map((f) => {
                            const active = newForm.standardFields.includes(f.type);
                            return (
                              <button
                                key={f.type}
                                type="button"
                                onClick={() => setNewForm((prev) => ({
                                  ...prev,
                                  standardFields: active ? prev.standardFields.filter((t) => t !== f.type) : [...prev.standardFields, f.type],
                                }))}
                                className={`text-[11px] font-semibold px-2 py-1 rounded-full border  whitespace-nowrap ${active ? 'bg-[#0176D3] border-[#0176D3] text-white' : 'border-[#D8DDE6] text-[#2E2E2E]'}`}
                              >
                                {f.label}
                              </button>
                            );
                          })}
                        </div>
                        {newForm.customQuestions.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {newForm.customQuestions.map((q) => (
                              <span key={q.key} className="bg-[#F4F6F9] border border-[#D8DDE6] rounded-full px-2 py-1 text-[11px] flex items-center gap-1">
                                {q.label}
                                <button onClick={() => setNewForm((f) => ({ ...f, customQuestions: f.customQuestions.filter((c) => c.key !== q.key) }))}><X size={10} /></button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-2 mt-2">
                          <input
                            className={INPUT}
                            value={newQuestionLabel}
                            onChange={(e) => setNewQuestionLabel(e.target.value)}
                            placeholder="Add a custom short-answer question, e.g. What's your budget?"
                          />
                          <button
                            type="button"
                            disabled={!newQuestionLabel.trim()}
                            onClick={() => {
                              const key = `custom_${Date.now()}`;
                              setNewForm((f) => ({ ...f, customQuestions: [...f.customQuestions, { key, label: newQuestionLabel.trim() }] }));
                              setNewQuestionLabel('');
                            }}
                            className="flex-shrink-0 bg-[#F4F6F9] border border-[#D8DDE6] hover:border-[#0176D3] text-[12px] font-semibold px-3 py-2 rounded-[4px] whitespace-nowrap"
                          >
                            + Add
                          </button>
                        </div>
                      </div>

                      <input className={INPUT} value={newForm.privacyPolicyUrl} onChange={(e) => setNewForm((f) => ({ ...f, privacyPolicyUrl: e.target.value }))} placeholder="Privacy policy URL (required by Meta)" />
                      <input className={INPUT} value={newForm.websiteUrl} onChange={(e) => setNewForm((f) => ({ ...f, websiteUrl: e.target.value }))} placeholder="Website for the thank-you screen (optional)" />
                      {newForm.privacyPolicyUrl && !privacyUrlOk && (
                        <p className="text-[11px] text-[#8E030F]">The privacy policy link needs to start with https:// — Meta rejects the form otherwise.</p>
                      )}
                      <p className="text-[11px] text-[#5C5C5C]">Leads land straight in your CRM as soon as someone submits.</p>
                      {newFormMissing.length > 0 && (
                        <p className="text-[11px] text-[#706E6B]">To create the form you still need: {newFormMissing.join(', ')}.</p>
                      )}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={createLeadForm}
                          disabled={creatingForm || newFormMissing.length > 0}
                          className="bg-[#0176D3] hover:bg-[#0153A0] disabled:opacity-50 text-white text-[12px] font-semibold px-3 py-1.5 rounded-[4px] whitespace-nowrap"
                        >
                          {creatingForm ? 'Creating…' : 'Create form'}
                        </button>
                        <button type="button" onClick={() => setShowNewForm(false)} className="text-[12px] text-[#5C5C5C] px-2">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className={LABEL}>Website link (required by Meta for lead ads)</label>
                  <input
                    id="ad-field-link"
                    className={INPUT}
                    value={creative.linkUrl}
                    onChange={(e) => setCreative((c) => ({ ...c, linkUrl: e.target.value }))}
                    placeholder="https://yourwebsite.com"
                  />
                  {creative.linkUrl && !isExternalHttpUrl(creative.linkUrl) ? (
                    <p className="text-[11px] text-[#8E030F] bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-2 py-1 mt-1">
                      Use your own website starting with https:// — Meta rejects a Facebook or Instagram page here.
                    </p>
                  ) : (
                    <p className="text-[11px] text-[#5C5C5C] mt-1">Where people land if they tap your name or logo. It must be your own website.</p>
                  )}
                </div>
                </>
              ) : isWhatsappAd ? (
                <p className="text-[12px] text-[#5C5C5C] bg-[#EAF4FE] border border-[#B8DAF3] rounded-[4px] px-3 py-2">
                  Tapping this ad opens a WhatsApp chat with <strong>{whatsappPhone || 'the number linked to your Page'}</strong>. No link is needed.
                </p>
              ) : (
                <div>
                  <label className={LABEL}>Destination Link{creative.callToAction === 'CALL_NOW' ? ' (optional for Call Now)' : ''}</label>
                  <input
                    id="ad-field-link"
                    className={INPUT}
                    value={creative.linkUrl}
                    onChange={(e) => setCreative((c) => ({ ...c, linkUrl: e.target.value }))}
                    placeholder="https://yourwebsite.com"
                  />
                  {creative.linkUrl && !isValidHttpUrl(creative.linkUrl) && (
                    <p className="text-[11px] text-[#8E030F] bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-2 py-1 mt-1">
                      This needs to be a real web address starting with https:// — Meta rejects a phone number or plain text here.
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className={LABEL}>Call to Action</label>
                <select className={INPUT} value={creative.callToAction} onChange={(e) => setCreative((c) => ({ ...c, callToAction: e.target.value }))}>
                  {(isWhatsappAd ? CTA_OPTIONS_WHATSAPP : isLeadCampaign ? CTA_OPTIONS_LEAD : CTA_OPTIONS_LINK).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {!isLeadCampaign && !isWhatsappAd && creative.callToAction === 'CALL_NOW' && (
                  <div className="mt-2">
                    <label className={LABEL}>Phone number to call</label>
                    <input
                      id="ad-field-phone"
                      className={INPUT}
                      value={creative.callPhone}
                      onChange={(e) => setCreative((c) => ({ ...c, callPhone: e.target.value }))}
                      placeholder="+971 50 123 4567"
                      inputMode="tel"
                    />
                    <p className="text-[11px] text-[#5C5C5C] mt-1">Meta doesn't show Call Now in Facebook Stories placements.</p>
                    {creative.callPhone && !isValidPhone(creative.callPhone) && (
                      <p className="text-[11px] text-[#8E030F] mt-1">Use digits only, optionally starting with + and the country code.</p>
                    )}
                  </div>
                )}
              </div>

              <div className="border border-[#D8DDE6] rounded-[4px] p-3 space-y-1" aria-label="Before you can create this ad">
                <p className="text-[11px] font-semibold text-[#706E6B]">{adBlocked ? 'Still needed to create this ad' : 'Ready to create'}</p>
                {adRequirements.map((r) => (
                  r.ok || !r.target ? (
                    <p key={r.label} className={`text-[12px] flex items-start gap-1.5 ${r.ok ? 'text-[#036B3A]' : 'text-[#8E030F]'}`}>
                      <span aria-hidden="true">{r.ok ? '✓' : '✗'}</span> {r.label}
                    </p>
                  ) : (
                    // A missing item is a button that scrolls to the field and focuses it.
                    <button
                      key={r.label}
                      type="button"
                      onClick={() => {
                        const el = document.getElementById(r.target);
                        el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                        (el?.matches('input,select,textarea') ? el : el?.querySelector('input:not([type="file"]),select,textarea,label'))?.focus?.({ preventScroll: true });
                      }}
                      className="text-[12px] flex items-start gap-1.5 text-[#8E030F] hover:underline text-left"
                    >
                      <span aria-hidden="true">✗</span> {r.label} <span className="text-[#706E6B]">— go to it</span>
                    </button>
                  )
                ))}
              </div>

              <div>
                <button
                  type="button"
                  onClick={openPreview}
                  disabled={previewLoading || (creative.format === 'carousel' ? !carouselCards.some((c) => c.imageHash) : (!imageHash || (creative.format === 'video' && !videoId)))}
                  className="w-full flex items-center justify-center gap-2 border border-[#D8DDE6] rounded-[4px] py-2 text-[12px] font-semibold text-[#0176D3] hover:bg-[#F4F6F9] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {previewLoading ? 'Rendering preview…' : 'Preview This Ad'}
                </button>
                <p className="text-[11px] text-[#5C5C5C] mt-1">See exactly how this ad will look before anything is created or spent.</p>
              </div>

              <div className="border border-[#D8DDE6] rounded-[4px] p-3 space-y-1 bg-[#F4F6F9]">
                <p className="text-[11px] font-semibold text-[#706E6B]">Review before creating</p>
                <p className="text-[12px] text-[#2E2E2E]"><strong>{campaign.name || 'Untitled campaign'}</strong> — {campaign.objective.replace('OUTCOME_', '').replace('_', ' ')}{campaign.specialAdCategories.length > 0 ? ` · ${campaign.specialAdCategories.join(', ')}` : ''}</p>
                <p className="text-[12px] text-[#2E2E2E]">{currencySymbol}{adSet.dailyBudget}/day · Ages {adSet.ageMin}-{adSet.ageMax}{adSet.genders.length ? ` · ${adSet.genders.includes(1) ? 'Men' : 'Women'}` : ''}{selectedLocations.length ? ` · ${selectedLocations.map((l) => l.name).join(', ')}` : ''}</p>
                <p className="text-[12px] text-[#2E2E2E]">{isLeadCampaign ? `Instant Form: ${leadForms.find((f) => f.id === creative.leadGenFormId)?.name || 'not selected yet'}` : isWhatsappAd ? `Opens WhatsApp chat: ${whatsappPhone || 'the number linked to your Page'}` : `Links to: ${creative.linkUrl || 'not set yet'}`}</p>
                <p className="text-[11px] text-[#5C5C5C] pt-1">This will be created paused — nothing spends until you review it and click Publish from the Campaigns list.</p>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-[#D8DDE6]">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || saving}
            className="flex items-center gap-1 text-[13px] font-semibold text-[#5C5C5C] hover:text-[#2E2E2E] disabled:opacity-40"
          >
            <ChevronLeft size={14} /> Back
          </button>

          {step === 0 && (
            <button onClick={submitCampaign} disabled={saving || !campaign.adAccountId || !campaign.name || (isWhatsappAd && !!whatsappPhone && !isValidPhone(whatsappPhone))} className="bg-[#0176D3] hover:bg-[#0153A0] disabled:opacity-50 text-white text-[13px] font-semibold px-4 py-2 rounded-[4px] whitespace-nowrap">
              {saving ? 'Creating…' : 'Create Campaign & Continue'}
            </button>
          )}
          {step === 1 && (
            <button
              onClick={submitAdSet}
              disabled={saving || !adSet.name || !creative.pageId || (!campaign.cbo && budgetType === 'lifetime' && (!adSet.lifetimeBudget || !adSet.endTime))}
              className="bg-[#0176D3] hover:bg-[#0153A0] disabled:opacity-50 text-white text-[13px] font-semibold px-4 py-2 rounded-[4px] whitespace-nowrap"
            >
              {saving ? 'Creating…' : 'Create Ad Set & Continue'}
            </button>
          )}
          {step === 2 && (
            <button
              onClick={submitAd}
              disabled={saving || adBlocked}
              className="bg-[#0176D3] hover:bg-[#0153A0] disabled:opacity-50 text-white text-[13px] font-semibold px-4 py-2 rounded-[4px] whitespace-nowrap"
            >
              {saving ? 'Creating…' : 'Create Ad & Finish'}
            </button>
          )}
        </div>
      </div>

      {previewHtml && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-[60] sm:p-4" onClick={() => setPreviewHtml(null)}>
          <div className="bg-white rounded-t-lg sm:rounded-lg w-full max-w-md max-h-[100dvh] overflow-y-auto shadow-[0_2px_8px_rgba(0,0,0,0.16)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
              <select
                className="text-[12px] border border-[#D8DDE6] rounded-[4px] px-2 py-1"
                value={previewFormat}
                onChange={(e) => { setPreviewFormat(e.target.value); fetchPreview(e.target.value); }}
              >
                {PREVIEW_FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <button onClick={() => setPreviewHtml(null)} className="text-[#5C5C5C] hover:text-[#2E2E2E]"><X size={18} /></button>
            </div>
            <div className="p-4 flex justify-center bg-[#F4F6F9] min-h-[300px] items-center">
              {previewLoading ? (
                <p className="text-[12px] text-[#5C5C5C]">Rendering preview…</p>
              ) : (
                // Meta's own returned iframe snippet — this app never
                // constructs the embedded markup itself, only forwards
                // exactly what generatepreviews/previews returned.
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
